import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { changeOrdersAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Loader2, Trash2, CheckCircle2, XCircle, Clock, ArrowUpRight, ArrowDownRight } from 'lucide-react';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800', icon: Clock },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
};

export default function ProjectChangeOrders({ projectId }) {
  const { isAdmin, isStaff } = useAuth();
  const canManage = isAdmin || isStaff;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', cost_impact: '' });
  const [responseNote, setResponseNote] = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      const res = await changeOrdersAPI.list(projectId);
      setOrders(res.data);
    } catch (err) {
      console.error('Failed to fetch change orders:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await changeOrdersAPI.create(projectId, { ...form, cost_impact: parseFloat(form.cost_impact) || 0 });
      setCreateOpen(false);
      setForm({ title: '', description: '', cost_impact: '' });
      fetchOrders();
    } catch (err) {
      console.error('Failed to create:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleAction = async (action) => {
    if (!selectedOrder) return;
    setFormLoading(true);
    try {
      await changeOrdersAPI.update(selectedOrder.change_order_id, { status: action, response_note: responseNote });
      setApproveOpen(false);
      setSelectedOrder(null);
      setResponseNote('');
      fetchOrders();
    } catch (err) {
      console.error('Failed to update:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (orderId) => {
    try {
      await changeOrdersAPI.delete(orderId);
      fetchOrders();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const totalImpact = orders.filter(o => o.status === 'approved').reduce((sum, o) => sum + (o.cost_impact || 0), 0);
  const pendingImpact = orders.filter(o => o.status === 'pending').reduce((sum, o) => sum + (o.cost_impact || 0), 0);

  if (loading) return <div className="animate-pulse h-48 bg-muted rounded" />;

  return (
    <div className="space-y-6" data-testid="project-change-orders">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Change Orders ({orders.length})</h3>
        {canManage && (
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="create-change-order-btn">
            <Plus className="h-4 w-4 mr-1" /> New Change Order
          </Button>
        )}
      </div>

      {/* Summary */}
      {orders.length > 0 && (
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardContent className="py-4">
              <p className="text-xs text-muted-foreground">Approved Cost Impact</p>
              <p className={`text-xl font-bold ${totalImpact >= 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="co-approved-impact">
                {totalImpact >= 0 ? '+' : ''}{totalImpact.toLocaleString('en-IN', {style:'currency',currency:'INR',maximumFractionDigits:0})}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <p className="text-xs text-muted-foreground">Pending Impact</p>
              <p className="text-xl font-bold text-amber-600" data-testid="co-pending-impact">
                {pendingImpact >= 0 ? '+' : ''}{pendingImpact.toLocaleString('en-IN', {style:'currency',currency:'INR',maximumFractionDigits:0})}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Orders list */}
      {orders.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground"><p className="text-sm">No change orders yet</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {orders.map(order => {
            const cfg = STATUS_CONFIG[order.status];
            const Icon = cfg.icon;
            const isPositive = order.cost_impact >= 0;
            return (
              <Card key={order.change_order_id} data-testid={`change-order-${order.change_order_id}`}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`p-2 rounded-full ${cfg.color}`}><Icon className="h-4 w-4" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{order.title}</p>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5">{order.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          By {order.created_by_name} — {formatDate(order.created_at)}
                        </p>
                        {order.response_note && (
                          <p className="text-xs mt-1 italic text-muted-foreground">Response: "{order.response_note}"</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`flex items-center gap-1 text-sm font-bold ${isPositive ? 'text-red-600' : 'text-emerald-600'}`}>
                        {isPositive ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                        {Math.abs(order.cost_impact).toLocaleString('en-IN', {style:'currency',currency:'INR',maximumFractionDigits:0})}
                      </div>
                      {canManage && order.status === 'pending' && (
                        <div className="flex gap-1 mt-2">
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setSelectedOrder(order); setResponseNote(''); setApproveOpen(true); }} data-testid={`review-co-${order.change_order_id}`}>
                            Review
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => handleDelete(order.change_order_id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New Change Order</DialogTitle>
            <DialogDescription>Record a change request with its cost impact</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required placeholder="e.g., Additional electrical points" data-testid="co-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="What changed and why..." rows={3} data-testid="co-desc-input" />
              </div>
              <div className="space-y-2">
                <Label>Cost Impact (+ for additional cost, - for savings)</Label>
                <Input type="number" step="0.01" value={form.cost_impact} onChange={e => setForm({...form, cost_impact: e.target.value})} required placeholder="e.g., 15000 or -5000" data-testid="co-cost-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading} data-testid="submit-co-btn">
                {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Approve/Reject Dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Review Change Order</DialogTitle>
            <DialogDescription>"{selectedOrder?.title}" — Cost: {selectedOrder?.cost_impact >= 0 ? '+' : ''}{selectedOrder?.cost_impact?.toLocaleString('en-IN', {style:'currency',currency:'INR'})}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">{selectedOrder?.description}</p>
            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Textarea value={responseNote} onChange={e => setResponseNote(e.target.value)} placeholder="Add a response note..." rows={2} />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setApproveOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleAction('rejected')} disabled={formLoading} data-testid="reject-co-btn">Reject</Button>
            <Button onClick={() => handleAction('approved')} disabled={formLoading} data-testid="approve-co-btn">
              {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
